import React, { useContext, useEffect } from 'react';
import { useS3ProxyApi } from 'src/hooks/useS3ProxyApi';
import { NavGraph, NavGraphItem } from 'src/types/navGraph';
import { NavGraphApiType } from 'src/types/useApi';

export const USE_NAV_GRAPH_V2 = ('false' as string) === 'true';
export const NAV_GRAPH_ROOT_NODE_ID_V1 = 357;
export const NAV_GRAPH_ROOT_NODE_ID_V2 = 21369;
export const NAV_GRAPH_ROOT_NODE_ID = USE_NAV_GRAPH_V2
    ? NAV_GRAPH_ROOT_NODE_ID_V2
    : NAV_GRAPH_ROOT_NODE_ID_V1;

export interface NavGraphContextType {
    navGraph: NavGraph;
    isLoading: boolean;
    hasError: boolean;
    getBreadcrumbNamesFromPath: (path?: string) => string[];
    getPathSegmentsByDocumentId: (
        documentId: number
    ) => { id: string; name: string }[] | undefined;
}

export const NavGraphContext = React.createContext<NavGraphContextType>({
    navGraph: {},
    isLoading: false,
    hasError: false,
    getBreadcrumbNamesFromPath: () => [],
    getPathSegmentsByDocumentId: () => undefined,
});

export const useNavGraph = () => useContext(NavGraphContext);

interface NavGraphProviderProps {
    children: React.ReactNode;
}

export const NavGraphProvider = ({ children }: NavGraphProviderProps) => {
    const apiEndpoint = USE_NAV_GRAPH_V2 ? 'v2/nav-graph' : 'nav-graph';

    const { makeRequest, data, error, loading }: NavGraphApiType =
        useS3ProxyApi({
            url: apiEndpoint,
            method: 'GET',
        });

    useEffect(() => {
        makeRequest();
    }, []);

    // v2: Unwrap root node to show its children directly
    const navGraphData = React.useMemo(() => {
        if (USE_NAV_GRAPH_V2 && data && data[NAV_GRAPH_ROOT_NODE_ID]) {
            return data[NAV_GRAPH_ROOT_NODE_ID].children || {};
        }
        return data;
    }, [data]);

    const getPathSegmentsByDocumentId = (documentId: number) => {
        if (!USE_NAV_GRAPH_V2 || !navGraphData) return undefined;

        const findDocument = (
            node: NavGraphItem,
            currentPath: { id: string; name: string }[]
        ): { id: string; name: string }[] | undefined => {
            if (node.documents) {
                for (const [docId, doc] of Object.entries(node.documents)) {
                    if ((doc as any).documentId === documentId) {
                        return currentPath;
                    }
                }
            }
            if (node.children) {
                for (const [childId, child] of Object.entries(node.children)) {
                    const result = findDocument(child, [
                        ...currentPath,
                        { id: childId, name: child.name },
                    ]);
                    if (result) return result;
                }
            }
            return undefined;
        };

        for (const [nodeId, node] of Object.entries(navGraphData)) {
            const result = findDocument(node, [
                { id: nodeId, name: node.name },
            ]);
            if (result) return result;
        }
        return undefined;
    };

    const getBreadcrumbNamesFromPath = (path?: string) => {
        const names: string[] = [];
        if (!navGraphData || !path) return names;

        if (USE_NAV_GRAPH_V2) {
            // v2: Path is slash-separated IDs, traverse using pathSegments
            const pathIds = path.split('/').filter(Boolean);
            // Remove root node ID if present
            if (
                pathIds[0] === String(NAV_GRAPH_ROOT_NODE_ID) ||
                pathIds[0] === '357'
            ) {
                pathIds.shift();
            }

            let currentNode: NavGraphItem | undefined;
            pathIds.forEach((id, index) => {
                currentNode =
                    index === 0
                        ? navGraphData[id]
                        : currentNode?.children?.[id];
                if (currentNode?.name) {
                    names.push(currentNode.name);
                }
            });
            return names;
        }

        const pathArray = path.split('/').filter(Boolean);
        //  remove first 357 path
        pathArray.shift();

        let currentBlock: NavGraphItem | undefined;

        pathArray.forEach((path, index) => {
            currentBlock =
                index === 0 ? data[path] : currentBlock?.children?.[path];
            if (currentBlock) {
                names.push(currentBlock.name);
            }
        });

        return names;
    };

    const value: NavGraphContextType = {
        navGraph: navGraphData,
        isLoading: loading,
        hasError: !!error,
        getBreadcrumbNamesFromPath,
        getPathSegmentsByDocumentId,
    };

    return (
        <NavGraphContext.Provider value={value}>
            {children}
        </NavGraphContext.Provider>
    );
};
